// Read-side helpers for screens. Each is a fold over admitted claims the viewer may see,
// built on the model's own views (interactions, liveOffers, standing, caseOf) wherever they exist.

import { EMBED, activeSanctions, interactions, liveOffers, standing, visibleTo } from '@model/index.ts';
import type { ActorId, Claim, ClaimId, Interaction } from '@model/index.ts';
import type { World } from './world.ts';

type Body = Record<string, unknown>;
export const body = (c: Claim): Body => (c.body && typeof c.body === 'object' && !Array.isArray(c.body) ? (c.body as Body) : {});
export const str = (c: Claim, k: string): string => String(body(c)[k] ?? '');
export const num = (c: Claim, k: string): number => Number(body(c)[k] ?? 0);

const all = (w: World): Claim[] => w.scope.log.map((e) => e.claim);
export const ofPred = (w: World, predicate: string): Claim[] => all(w).filter((c) => c.predicate === predicate);
const refersTo = (w: World, id: ClaimId, predicates: string[]): Claim[] => all(w).filter((c) => predicates.includes(c.predicate) && c.refs.includes(id));

export const visibleIds = (w: World, viewer: ActorId): Set<ClaimId> => new Set(w.scope.visible(viewer).map((c) => c.id));
export const canSee = (w: World, viewer: ActorId, c: Claim): boolean => visibleTo(c, viewer);
export const claim = (w: World, id: ClaimId | undefined): Claim | undefined => (id ? w.scope.entry(id)?.claim : undefined);
export const seqOf = (w: World, id: ClaimId): number | undefined => w.scope.entry(id)?.seq;

const params = (w: World) => w.scope.policy.params as Record<string, unknown>;
export const stewards = (w: World): ActorId[] => (params(w).stewards as ActorId[]) ?? [];
export const triage = (w: World): ActorId[] => (params(w).triage as ActorId[]) ?? [];
export const param = (w: World, k: string): number => Number(params(w)[k] ?? 0);

// Who may read a report: the reporter, the stewards and the triage agent. Never the person reported.
export const caseAudience = (w: World, reporter: ActorId): ActorId[] => [...new Set([reporter, ...stewards(w), ...triage(w)])];
// Promoted private messages: stewards and the reporter only. The AI does not read private messages.
export const proofAudience = (w: World, reporter: ActorId): ActorId[] => [...new Set([reporter, ...stewards(w)])];

export const isMember = (w: World, id: ActorId): boolean => ofPred(w, 'join').some((c) => c.author === id);

export function joinInfo(w: World, id: ActorId): { at: number; voucher: ActorId | null } | null {
  const j = ofPred(w, 'join').find((c) => c.author === id);
  if (!j) return null;
  const viaPhone = ofPred(w, 'verified.phone').some((c) => c.subject === id);
  const vouch = ofPred(w, 'vouch').find((c) => c.subject === id);
  return { at: j.issuedAt, voucher: viaPhone ? null : (vouch?.author ?? null) };
}

export const vouchesFor = (w: World, id: ActorId): Claim[] => ofPred(w, 'vouch').filter((c) => c.subject === id);

export type OfferState = 'live' | 'agreed' | 'down';
// An accept stands until either party releases it (R8); a released accept frees the item.
const released = (w: World, acceptId: ClaimId): boolean => refersTo(w, acceptId, ['release']).length > 0;
export function offerState(w: World, offerId: ClaimId): OfferState {
  const by = refersTo(w, offerId, ['accept', 'withdraw']);
  if (by.some((c) => c.predicate === 'withdraw')) return 'down';
  if (by.some((c) => c.predicate === 'accept' && !released(w, c.id))) return 'agreed';
  return 'live';
}
export const acceptOf = (w: World, offerId: ClaimId): Claim | undefined =>
  refersTo(w, offerId, ['accept']).filter((a) => !released(w, a.id)).at(-1);
export const releaseOf = (w: World, acceptId: ClaimId): Claim | undefined => refersTo(w, acceptId, ['release'])[0];

// A person's own items still for sale, paused or not (the kernel's liveOffers hides a paused seller from everyone).
export const myLive = (w: World, id: ActorId): Claim[] =>
  ofPred(w, 'offer')
    .filter((o) => o.author === id && offerState(w, o.id) === 'live')
    .sort((a, b) => b.issuedAt - a.issuedAt);

// A steward's pause in force, as the kernel defines it (R5: unexpired, not superseded by an appeal decision),
// cut to what the viewer may see.
export const activePause = (w: World, subject: ActorId, viewer: ActorId): Claim | undefined =>
  activeSanctions(w.scope, subject, w.now())
    .filter((d) => canSee(w, viewer, d))
    .sort((a, b) => num(b, 'until') - num(a, 'until'))[0];
// The latest pause that ran to its end (not superseded).
export function endedPause(w: World, subject: ActorId): Claim | undefined {
  const now = w.now();
  return ofPred(w, 'decide')
    .filter((d) => d.subject === subject && str(d, 'outcome') === 'suspend-offers' && num(d, 'until') <= now && !refersTo(w, d.id, ['decide']).length)
    .sort((a, b) => num(b, 'until') - num(a, 'until'))[0];
}

// The feed is the kernel's view: live items the viewer may see, paused sellers already left out (R5).
export const feed = (w: World, viewer: ActorId): Claim[] => [...liveOffers(w.scope, viewer, w.now())].sort((a, b) => b.issuedAt - a.issuedAt);

export const tradesOf = (w: World, id: ActorId): Interaction[] =>
  interactions(w.scope)
    .filter((i) => i.buyer === id || i.seller === id)
    .reverse();
export const trade = (w: World, acceptId: ClaimId): Interaction | undefined => interactions(w.scope).find((i) => i.id === acceptId);
export const completedCount = (w: World, id: ActorId): number => standing(w.scope, id, w.now()).completed.length;

export interface Said {
  rate: Claim;
  reply: Claim | null;
}
export const saidAbout = (w: World, subject: ActorId): Said[] =>
  ofPred(w, 'rate')
    .filter((r) => r.subject === subject)
    .map((r) => ({ rate: r, reply: refersTo(w, r.id, ['respond'])[0] ?? null }))
    .reverse();
export const ratingBy = (w: World, acceptId: ClaimId, author: ActorId): Claim | undefined =>
  ofPred(w, 'rate').find((r) => r.refs[0] === acceptId && r.author === author);

// Conversations: the inquiry is on the record for its two parties; messages live on their devices.
export const inquiries = (w: World, viewer: ActorId): Claim[] =>
  w.scope
    .visible(viewer)
    .filter((c) => c.predicate === 'inquire' && c.refs[0])
    .sort((a, b) => lastActivity(w, b) - lastActivity(w, a));
export const thread = (w: World, viewer: ActorId, inquiryId: ClaimId): Claim[] =>
  w.device
    .filter((m) => m.refs[0] === inquiryId && (m.author === viewer || (Array.isArray(m.audience) && m.audience.includes(viewer))))
    .sort((a, b) => a.issuedAt - b.issuedAt);
const lastActivity = (w: World, inq: Claim): number => Math.max(inq.issuedAt, ...w.device.filter((m) => m.refs[0] === inq.id).map((m) => m.issuedAt));
export const inquiryBy = (w: World, buyer: ActorId, offerId: ClaimId): Claim | undefined =>
  w.scope.visible(buyer).find((c) => c.predicate === 'inquire' && c.author === buyer && c.refs[0] === offerId);
export function otherParty(w: World, inq: Claim, viewer: ActorId): ActorId {
  if (inq.author !== viewer) return inq.author;
  return claim(w, inq.refs[0])?.author ?? '';
}
// Messages a reporter could share: everything in their chats with the subject.
export const messagesWith = (w: World, viewer: ActorId, other: ActorId): Claim[] =>
  w.device
    .filter(
      (m) =>
        (m.author === viewer && Array.isArray(m.audience) && m.audience.includes(other)) ||
        (m.author === other && Array.isArray(m.audience) && m.audience.includes(viewer)),
    )
    .sort((a, b) => a.issuedAt - b.issuedAt);

// Reports, decisions and appeals.
export const decisionOn = (w: World, reportId: ClaimId): Claim | undefined => refersTo(w, reportId, ['decide'])[0];
// The decision that answered an appeal of this decision, if any (it supersedes the original).
export const appealOf = (w: World, decisionId: ClaimId): Claim | undefined => refersTo(w, decisionId, ['appeal'])[0];
export const appealDecision = (w: World, appealId: ClaimId): Claim | undefined => refersTo(w, appealId, ['decide'])[0];
export const supersededBy = (w: World, decisionId: ClaimId): Claim | undefined => refersTo(w, decisionId, ['decide'])[0];
// Follow appeals to the decision that now stands.
export function finalDecision(w: World, d: Claim): Claim {
  let cur = d;
  for (let next = supersededBy(w, cur.id); next; next = supersededBy(w, cur.id)) cur = next;
  return cur;
}
export const repliesTo = (w: World, id: ClaimId, viewer: ActorId): Claim[] => refersTo(w, id, ['respond']).filter((c) => canSee(w, viewer, c));
export const isAppealDecision = (w: World, d: Claim): boolean => claim(w, d.refs[0])?.predicate === 'appeal';
export const reportsBy = (w: World, reporter: ActorId): Claim[] => ofPred(w, 'report').filter((r) => r.author === reporter).reverse();
export const openReportBy = (w: World, reporter: ActorId, subject: ActorId): Claim | undefined =>
  reportsBy(w, reporter).find((r) => r.subject === subject && !decisionOn(w, r.id));
export const proofFor = (w: World, reportId: ClaimId, viewer: ActorId): Claim[] =>
  refersTo(w, reportId, ['promote']).filter((c) => canSee(w, viewer, c));
export const flagsFor = (w: World, reportId: ClaimId, viewer: ActorId): Claim[] =>
  refersTo(w, reportId, ['flag']).filter((c) => canSee(w, viewer, c));
// Appeals a steward can see that no decision has answered yet.
export const openAppeals = (w: World, viewer: ActorId): Claim[] =>
  w.scope.visible(viewer).filter((c) => c.predicate === 'appeal' && !appealDecision(w, c.id));

export interface Case {
  subject: ActorId;
  reports: Claim[];
  proof: Claim[];
  flags: Claim[];
  risk: number;
}
// A steward's case groups every open report about one person (the model's caseOf is per report).
export function openCases(w: World, viewer: ActorId): Case[] {
  const open = w.scope.visible(viewer).filter((c) => c.predicate === 'report' && !decisionOn(w, c.id));
  const by = new Map<ActorId, Claim[]>();
  for (const r of open) by.set(r.subject!, [...(by.get(r.subject!) ?? []), r]);
  return [...by.entries()]
    .map(([subject, reports]) => {
      const proof = reports.flatMap((r) => proofFor(w, r.id, viewer));
      const flags = reports.flatMap((r) => flagsFor(w, r.id, viewer));
      return { subject, reports, proof, flags, risk: Math.max(0, ...flags.map((f) => num(f, 'risk'))) };
    })
    .sort((a, b) => b.risk - a.risk || b.reports.length - a.reports.length);
}
export const distinctReporters = (reports: Claim[]): number => new Set(reports.map((r) => r.author)).size;

export const caseReasonKey = (code: string): string =>
  ['advance', 'noshow', 'notAsDescribed', 'abuse'].includes(code) ? `case.r.${code}` : 'case.r.other';
export const reasonKey = (code: string) =>
  (({ advance: 'report.r.advance', noshow: 'report.r.noshow', notAsDescribed: 'report.r.notAsDescribed', abuse: 'report.r.abuse' }) as Record<string, string>)[code] ??
  'report.r.other';

// A short, stable code a newcomer shows a neighbour so they can vouch. Derived from the public key.
export function codeOf(id: ActorId): string {
  const alphabet = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
  let n = 0;
  for (const ch of id.slice(8)) n = (n * 31 + ch.charCodeAt(0)) >>> 0;
  let out = '';
  for (let i = 0; i < 6; i++) {
    out += alphabet[n % alphabet.length];
    n = Math.floor(n / alphabet.length) + (i + 1) * 7919;
  }
  return `${out.slice(0, 3)}-${out.slice(3)}`;
}

// Where a promoted message sits inside a promote claim's body: the kernel's declared embedding key (v2: $claim).
export const promoteBody = (msg: Claim): Record<string, unknown> => ({ [EMBED]: msg });
export const embedded = (promote: Claim): Claim | undefined => (body(promote)[EMBED] as Claim | undefined) ?? undefined;
